import React, { useState, useEffect } from 'react';
import {
  Modal,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Row,
  Col,
  Badge,
  Card,
  CardBody,
  Spinner,
} from 'reactstrap';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { formatCreatedDate } from '../constants/utils';
import api from '../helpers/api';

const VendorModal = ({ isOpen, toggle, vendorData, loading, onEditVendor = null }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [assetWarrantyData, setAssetWarrantyData] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);

  const fetchAssetWarrantyData = async () => {
    if (!vendorData?.vendorName) return;

    setAssetsLoading(true);
    try {
      const { data } = await api.get(`asset-warranty-maintenance/get-all?vendorName=${encodeURIComponent(vendorData.vendorName)}&count=0`);
      setAssetWarrantyData(data?.response || []);
    } catch (error) {
      console.error('Error fetching asset warranty data:', error);
      setAssetWarrantyData([]);
    } finally {
      setAssetsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'assets' && vendorData?.vendorName) {
      fetchAssetWarrantyData();
    }
  }, [activeTab, vendorData?.vendorName]);

  const getStatusBadgeColor = (status) => {
    const statusColors = {
      'Active': 'success',
      'Expired': 'danger',
      'Expiring Soon': 'warning',
      'Partially Covered': 'info'
    };
    return statusColors[status] || 'secondary';
  };

  if (!vendorData && !loading) return null;

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Enhanced Header */}
        <div
          className="modal-header border-0 bg-gradient-primary text-white position-sticky"
          style={{
            top: 0,
            zIndex: 10,
            // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
            padding: '1.5rem'
          }}
        >
          <div className="d-flex align-items-center">
            <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-3"
                 style={{ width: '40px', height: '40px' }}>
              <i className="bx bx-user text-white fs-5"></i>
            </div>
            <div>
              <h5 className="modal-title mb-0 fw-bold text-white">
                {vendorData?.vendorName || 'Vendor Details'}
              </h5>
              <p className="text-white-50 small mb-0">Vendor information and details</p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 ms-auto">
            {onEditVendor && vendorData && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                onClick={() => onEditVendor(vendorData)}
                title="Edit Vendor"
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  color: '#495057',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  borderRadius: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <FeatherIcon icon="edit-3" size={16} />
                <span>Edit Vendor</span>
              </button>
            )}
            <button
              onClick={toggle}
              type="button"
              className="btn-close btn-close-white"
              aria-label="Close"
            />
          </div>
        </div>

        {/* Navigation Tabs */}
        <Nav
          tabs
          className="border-bottom-0 px-4 pt-3 bg-light"
          style={{ borderRadius: '0' }}
        >
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'info' ? '#fff' : 'transparent',
                color: activeTab === 'info' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'info' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              <i className="bx bx-info-circle me-2"></i>
              Info
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'assets' ? 'active' : ''}`}
              onClick={() => setActiveTab('assets')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'assets' ? '#fff' : 'transparent',
                color: activeTab === 'assets' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'assets' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              <i className="bx bx-package me-2"></i>
              Assets
            </NavLink>
          </NavItem>
        </Nav>

        {/* Modal Body */}
        <div 
          className="modal-body p-0" 
          style={{ 
            maxHeight: 'calc(90vh - 200px)',
            overflow: 'auto'
          }}
        >
          <TabContent activeTab={activeTab} className="h-100">
            {/* Info Tab */}
            <TabPane tabId="info">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="info-icon me-3">
                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                             style={{ width: '40px', height: '40px' }}>
                          <i className="bx bx-user text-primary fs-5"></i>
                        </div>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold">Vendor Information</h5>
                        <p className="text-muted small mb-0">Complete vendor details and contact information</p>
                      </div>
                    </div>

                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" size="lg" />
                        <p className="text-muted mt-3">Loading vendor details...</p>
                      </div>
                    ) : (
                      vendorData && (
                        <Row className="g-4">
                          {/* Basic Information */}
                          <Col md={6}>
                            <div className="info-card bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-info-circle me-2"></i>Basic Information
                              </h6>
                              <div className="info-item mb-3">
                                <label className="text-muted small">Vendor Name</label>
                                <div className="fw-medium">{vendorData?.vendorName || 'N/A'}</div>
                              </div>
                              <div className="info-item mb-3">
                                <label className="text-muted small">Vendor Type</label>
                                <div>
                                  <Badge color="primary" className="bg-opacity-10 text-primary border-0">
                                    {vendorData?.vendorType
                                      ?.replace(/_/g, " ")
                                      .toLowerCase()
                                      .replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}
                                  </Badge>
                                </div>
                              </div>
                              <div className="info-item mb-3">
                                <label className="text-muted small">Vendor Role</label>
                                <div>
                                  <Badge color="info" className="bg-opacity-10 text-info border-0">
                                    {vendorData?.vendorRole || 'N/A'}
                                  </Badge>
                                </div>
                              </div>
                              <div className="info-item">
                                <label className="text-muted small">Created Date</label>
                                <div className="fw-medium">{formatCreatedDate(vendorData?.createdAt) || 'N/A'}</div>
                              </div>
                            </div>
                          </Col>

                          {/* Contact Information */}
                          <Col md={6}>
                            <div className="info-card bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-phone me-2"></i>Contact Information
                              </h6>
                              <div className="info-item mb-3">
                                <label className="text-muted small">Email</label>
                                <div className="fw-medium d-flex align-items-center">
                                  <FeatherIcon icon="mail" size={16} className="me-2 text-muted" />
                                  {vendorData?.email ? (
                                    <a href={`mailto:${vendorData.email}`} className="text-decoration-none">
                                      {vendorData.email}
                                    </a>
                                  ) : (
                                    <span>N/A</span>
                                  )}
                                </div>
                              </div>
                              <div className="info-item mb-3">
                                <label className="text-muted small">Phone Number</label>
                                <div className="fw-medium d-flex align-items-center">
                                  <FeatherIcon icon="phone" size={16} className="me-2 text-muted" />
                                  {vendorData?.phoneNumber ? (
                                    <a href={`tel:${vendorData.phoneNumber}`} className="text-decoration-none">
                                      {vendorData.phoneNumber}
                                    </a>
                                  ) : (
                                    <span>N/A</span>
                                  )}
                                </div>
                              </div>
                              <div className="info-item">
                                <label className="text-muted small">Address</label>
                                <div className="fw-medium d-flex align-items-start">
                                  <FeatherIcon icon="map-pin" size={16} className="me-2 mt-1 text-muted flex-shrink-0" />
                                  <div>{vendorData?.address || 'N/A'}</div>
                                </div>
                              </div>
                            </div>
                          </Col>

                          {/* Additional Information */}
                          {vendorData?.remarks && (
                            <Col md={12}>
                              <div className="info-card bg-light rounded p-3">
                                <h6 className="text-primary mb-3 fw-semibold">
                                  <i className="bx bx-file-text me-2"></i>Additional Information
                                </h6>
                                <div className="info-item">
                                  <label className="text-muted small">Remarks</label>
                                  <div className="fw-medium">{vendorData.remarks}</div>
                                </div>
                              </div>
                            </Col>
                          )}
                        </Row>
                      )
                    )}
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Assets Tab */}
            <TabPane tabId="assets">
              <div className="p-4">
                {assetsLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" size="lg" />
                    <p className="text-muted mt-3">Loading asset warranty data...</p>
                  </div>
                ) : assetWarrantyData.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i className="bx bx-package text-muted" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <p className="text-muted">No assets found for this vendor</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Asset ID</th>
                          <th>Manufacturer Warranty</th>
                          <th>Warranty Extension</th>
                          <th>Maintenance Contract</th>
                          <th>Contract Details</th>
                          <th>Overall Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assetWarrantyData.map((asset, index) => (
                          <tr key={asset.id}>
                            <td>{index + 1}</td>
                            <td>
                              <div className="fw-medium">{asset.assetId}</div>
                            </td>
                            <td>
                              <div className="mb-1">
                                {asset.hasManufacturerWarranty ? (
                                  <>
                                    <Badge color={getStatusBadgeColor(asset.manufacturerWarrantyStatus)} className="mb-1">
                                      {asset.manufacturerWarrantyStatus}
                                    </Badge>
                                    <div className="small text-muted">
                                      <div>{new Date(asset.manufacturerWarrantyStartDate).toLocaleDateString()} - {new Date(asset.manufacturerWarrantyEndDate).toLocaleDateString()}</div>
                                      <div>{asset.manufacturerWarrantyDaysRemaining} days remaining</div>
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-muted small">No warranty</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="mb-1">
                                {asset.hasWarrantyExtension ? (
                                  <>
                                    <Badge color={getStatusBadgeColor(asset.warrantyExtensionStatus)} className="mb-1">
                                      {asset.warrantyExtensionStatus}
                                    </Badge>
                                    <div className="small text-muted">
                                      <div>{new Date(asset.warrantyExtensionStartDate).toLocaleDateString()} - {new Date(asset.warrantyExtensionEndDate).toLocaleDateString()}</div>
                                      <div>{asset.warrantyExtensionDaysRemaining} days remaining</div>
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-muted small">No extension</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="mb-1">
                                <div className="fw-medium">{asset.maintenanceContractTypeDisplay}</div>
                                <div className="small text-muted">Vendor: {asset.maintenanceVendorName}</div>
                                <Badge color={getStatusBadgeColor(asset.contractStatus)}>
                                  {asset.contractStatus}
                                </Badge>
                              </div>
                            </td>
                            <td>
                              <div className="small">
                                <div className="mb-1"><strong>Contract #:</strong> {asset.contractNumber}</div>
                                <div className="mb-1"><strong>Period:</strong> {new Date(asset.contractStartDate).toLocaleDateString()} - {new Date(asset.contractEndDate).toLocaleDateString()}</div>
                                <div className="mb-1"><strong>Days Remaining:</strong> {asset.contractDaysRemaining}</div>
                                <div className="mb-1"><strong>Value:</strong> {asset.contractValueFormatted}</div>
                                {asset.notes && (
                                  <div className="mt-2 p-2 bg-light rounded">
                                    <strong>Notes:</strong> {asset.notes}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <Badge color={getStatusBadgeColor(asset.overallStatus)} className="mb-1">
                                {asset.overallStatus}
                              </Badge>
                              {asset.expiringSoon && (
                                <div className="small">
                                  <Badge color="warning" className="mt-1">
                                    <i className="bx bx-time-five me-1"></i>
                                    Expiring Soon
                                  </Badge>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabPane>
          </TabContent>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-secondary" onClick={toggle}>
            Close
          </button>
        </div>
      </motion.div>
    </Modal>
  );
};

export default VendorModal;